import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField,
} from '@mui/material';
import iziToast from 'izitoast';

import { expelStudent, getGroups, transferStudent } from '../../actions/journal';
import { internalServerError } from '../../utils/consts/pathRoutes';
import useIsMobile, { mobileDialogProps } from '../../utils/useIsMobile';
import { studentName, todayString } from '../../utils/journalFormat';

/**
 * Перевод и отчисление.
 *
 * Комментарий обязателен в обоих случаях — это требование заказчика: по комментарию потом
 * восстанавливают, почему слушатель оказался в другой группе или выбыл.
 */
const MoveStudentModal = ({ state, currentGroupId, onClose, onDone }) => {
  const isMobile = useIsMobile();
  const open = Boolean(state);
  const isTransfer = state?.mode === 'transfer';

  const [groups, setGroups] = useState([]);
  const [target, setTarget] = useState(null);
  const [date, setDate] = useState(todayString());
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setTarget(null);
    setDate(todayString());
    setComment('');

    if (isTransfer && groups.length === 0) {
      getGroups({ status: 'Набор' })
        .then((items) => setGroups(items))
        .catch(() => iziToast.error({ message: 'Не удалось загрузить группы', position: 'topRight' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isTransfer]);

  if (!open) return null;

  const options = groups
    .filter((group) => group.id !== currentGroupId)
    .map((group) => ({
      id: group.id,
      label: `${group.name} · ${group.students_count}/${group.capacity} мест занято`,
    }));

  const submit = () => {
    setSaving(true);

    const request = isTransfer
      ? transferStudent(state.membership.membership_id, {
        to_group_id: target.id,
        date,
        comment,
      })
      : expelStudent(state.membership.membership_id, { date, comment });

    request
      .then((result) => {
        iziToast.success({
          message: isTransfer
            ? `Слушатель переведён, перенесено часов: ${result.hours_carried_over}`
            : 'Слушатель отчислен',
          position: 'topRight',
        });
        onDone();
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth {...mobileDialogProps(isMobile)}>
      <DialogTitle>
        {isTransfer ? 'Перевод в другую группу' : 'Отчисление'}
        {' — '}
        {studentName(state.membership)}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isTransfer && (
            <Alert severity="info">
              Накопленные часы перенесутся в новую группу суммарно.
              История отметок останется в текущей группе.
            </Alert>
          )}

          {isTransfer && (
            <Autocomplete
              options={options}
              value={target}
              onChange={(event, value) => setTarget(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Новая группа" />}
            />
          )}

          <TextField
            label={isTransfer ? 'Дата перевода' : 'Дата отчисления'}
            type="date" value={date} onChange={(event) => setDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Комментарий" required value={comment} multiline rows={2}
            onChange={(event) => setComment(event.target.value)}
            placeholder={isTransfer ? 'Причина перевода' : 'Причина отчисления'}
            helperText="Обязательное поле"
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          variant="contained" color={isTransfer ? 'primary' : 'error'}
          onClick={submit}
          disabled={saving || !comment.trim() || (isTransfer && !target)}
        >
          {isTransfer ? 'Перевести' : 'Отчислить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveStudentModal;
